import { ServiceConnectionType } from './service.connection';

export type SecretFlagMap = { [path: string]: boolean };

export interface ServiceConnectionSecretAware {
    secrets?: SecretFlagMap;
}

export interface ServiceConnectionRedactionRule {
    tokensExposed?: string[];
    settingsSecrets?: string[];
    dataExposed?: string[];

    editableTokenSecrets?: string[];
    editableSettingsSecrets?: string[];
    editableDataSecrets?: string[];
}

export const SERVICE_CONNECTION_REDACTION_RULES: { [k in ServiceConnectionType]?: ServiceConnectionRedactionRule } = {
    [ServiceConnectionType.MYOBAcumatica]: {
        settingsSecrets: ['password', 'clientSecret'],
        editableSettingsSecrets: ['password', 'clientSecret']
    },
    [ServiceConnectionType.Jobpac]: {
        settingsSecrets: ['password', 'key'],
        editableSettingsSecrets: ['password', 'key']
    },
    [ServiceConnectionType.WBImporter]: {
    },
    [ServiceConnectionType.SMS]: {
        tokensExposed: ['provider', 'longCodes'],
        editableTokenSecrets: ['apiKey', 'apiSecret']
    },
    [ServiceConnectionType.HomesNSW]: {
        tokensExposed: ['endpoint', 'orgId'],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.BGISHomesVIC]: {
        tokensExposed: ['endpoint', 'orgId', 'authMode'],
        editableTokenSecrets: ['key', 'secret', 'apiKey']
    },
    [ServiceConnectionType.BGISManhattanONE]: {
        tokensExposed: ['middlewareUrl', 'key', 'authorisationUrl'],
        editableTokenSecrets: ['secret'],
        // The contractor sync card shows the last run, a queued "Sync now" and when the schedule runs next
        dataExposed: ['contractorSync', 'contractorSyncRequest', 'contractorSyncLastAttemptAt']
    },
    [ServiceConnectionType.AddressValidation]: {
        tokensExposed: ['endpoint'],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.PropertyMe]: {
        tokensExposed: [],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.PropertyMeCustomer]: {
        tokensExposed: [],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.PropertyTree]: {
        tokensExposed: [],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.PropertyTreeCustomer]: {
        tokensExposed: [],
        editableTokenSecrets: ['key', 'secret']
    },
    [ServiceConnectionType.OpenAI]: { editableTokenSecrets: [''] },
    [ServiceConnectionType.Anthropic]: { editableTokenSecrets: [''] },
    [ServiceConnectionType.What3Words]: { editableTokenSecrets: [''] },
    [ServiceConnectionType.WorkBuddy]: {},
    [ServiceConnectionType.Xero]: {},
    [ServiceConnectionType.QuickBooks]: {},
    [ServiceConnectionType.Myob]: {},
    [ServiceConnectionType.Stripe]: {}
};

function getRule(type: ServiceConnectionType): ServiceConnectionRedactionRule {
    return SERVICE_CONNECTION_REDACTION_RULES[type] ?? {};
}

function getValueAtPath(obj: any, path: string): any {
    if (obj == null) return undefined;
    if (path === '') return obj;
    return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
}

function deletePathOnClone(obj: any, path: string): void {
    if (obj == null) return;
    const keys = path.split('.');
    let cursor = obj;
    for (let i = 0; i < keys.length - 1; i++) {
        if (cursor[keys[i]] == null) return;
        cursor = cursor[keys[i]];
    }
    delete cursor[keys[keys.length - 1]];
}

function isMeaningfulValue(value: any): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string' && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
}

function pruneToWhitelist(parent: any, allowed: string[]): any {
    if (parent == null || typeof parent !== 'object' || Array.isArray(parent)) return null;
    if (allowed.length === 0) return null;
    const pruned: any = {};
    for (const key of allowed) {
        if (parent[key] !== undefined) {
            pruned[key] = parent[key];
        }
    }
    return Object.keys(pruned).length ? pruned : null;
}

export function redactSecrets<T extends { type?: ServiceConnectionType }>(item: T): T & ServiceConnectionSecretAware {
    if (item == null) return item as T & ServiceConnectionSecretAware;
    const original: any = item;
    const clone: any = JSON.parse(JSON.stringify(item));
    const rule = getRule(original.type);
    const flags: SecretFlagMap = {};

    if (rule.editableTokenSecrets) {
        for (const path of rule.editableTokenSecrets) {
            const fullPath = path === '' ? 'tokens' : `tokens.${path}`;
            if (isMeaningfulValue(getValueAtPath(original, fullPath))) {
                flags[fullPath] = true;
            }
        }
    }
    if (rule.editableSettingsSecrets) {
        for (const path of rule.editableSettingsSecrets) {
            const fullPath = `settings.${path}`;
            if (isMeaningfulValue(getValueAtPath(original, fullPath))) {
                flags[fullPath] = true;
            }
        }
    }
    if (rule.editableDataSecrets) {
        for (const path of rule.editableDataSecrets) {
            const fullPath = `data.${path}`;
            if (isMeaningfulValue(getValueAtPath(original, fullPath))) {
                flags[fullPath] = true;
            }
        }
    }

    if (rule.tokensExposed !== undefined) {
        clone.tokens = pruneToWhitelist(clone.tokens, rule.tokensExposed);
    } else {
        delete clone.tokens;
    }

    if (rule.dataExposed !== undefined) {
        clone.data = pruneToWhitelist(clone.data, rule.dataExposed);
    } else {
        delete clone.data;
    }

    if (rule.settingsSecrets) {
        for (const subPath of rule.settingsSecrets) {
            deletePathOnClone(clone, `settings.${subPath}`);
        }
    }

    clone.secrets = flags;
    return clone as T & ServiceConnectionSecretAware;
}

export function stripUnchangedSecrets<T extends { type?: ServiceConnectionType }>(item: Partial<T>): Partial<T> {
    if (item == null || (item as any).type == null) return item;
    const clone: any = JSON.parse(JSON.stringify(item));
    const rule = getRule((item as any).type);
    const paths: string[] = [];
    if (rule.editableTokenSecrets) {
        for (const p of rule.editableTokenSecrets) paths.push(p === '' ? 'tokens' : `tokens.${p}`);
    }
    if (rule.editableSettingsSecrets) {
        for (const p of rule.editableSettingsSecrets) paths.push(`settings.${p}`);
    }
    if (rule.editableDataSecrets) {
        for (const p of rule.editableDataSecrets) paths.push(`data.${p}`);
    }
    for (const path of paths) {
        if (!isMeaningfulValue(getValueAtPath(clone, path))) {
            deletePathOnClone(clone, path);
        }
    }
    return clone as Partial<T>;
}
